import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { notificationsAPI } from '../../services/api';
import { Notification, CreateNotificationRequest, PaginatedNotificationResponse } from '../../types/notification';

interface NotificationsState {
  activeNotifications: Notification[];
  allNotifications: Notification[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  activeNotifications: [],
  allNotifications: [],
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  loading: false,
  error: null,
};

// Async thunks
export const fetchActiveNotifications = createAsyncThunk(
  'notifications/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationsAPI.getActive();
      return response.data as Notification[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active notifications');
    }
  }
);

export const fetchAllNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (params: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await notificationsAPI.getAll(params);
      return response.data as PaginatedNotificationResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

export const createNotification = createAsyncThunk(
  'notifications/create',
  async (data: CreateNotificationRequest, { rejectWithValue }) => {
    try {
      const response = await notificationsAPI.create(data);
      return response.data as Notification;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create notification');
    }
  }
);

export const archiveNotification = createAsyncThunk(
  'notifications/archive',
  async (id: string, { rejectWithValue }) => {
    try {
      await notificationsAPI.archive(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to archive notification');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await notificationsAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete notification');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch active notifications
    builder.addCase(fetchActiveNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchActiveNotifications.fulfilled, (state, action: PayloadAction<Notification[]>) => {
      state.loading = false;
      state.activeNotifications = action.payload;
    });
    builder.addCase(fetchActiveNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch all notifications
    builder.addCase(fetchAllNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAllNotifications.fulfilled, (state, action: PayloadAction<PaginatedNotificationResponse>) => {
      state.loading = false;
      state.allNotifications = action.payload.data;
      state.totalItems = action.payload.total_items;
      state.totalPages = action.payload.total_pages;
      state.currentPage = action.payload.page;
    });
    builder.addCase(fetchAllNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create notification
    builder.addCase(createNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
      state.loading = false;
      state.allNotifications = [action.payload, ...state.allNotifications];
      if (action.payload.is_active) {
        state.activeNotifications = [action.payload, ...state.activeNotifications];
      }
    });
    builder.addCase(createNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Archive notification
    builder.addCase(archiveNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(archiveNotification.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.allNotifications = state.allNotifications.map(n =>
        n.id === action.payload ? { ...n, archived: true, is_active: false } : n
      );
      state.activeNotifications = state.activeNotifications.filter(n => n.id !== action.payload);
    });
    builder.addCase(archiveNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete notification
    builder.addCase(deleteNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteNotification.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.allNotifications = state.allNotifications.filter(n => n.id !== action.payload);
      state.activeNotifications = state.activeNotifications.filter(n => n.id !== action.payload);
    });
    builder.addCase(deleteNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError } = notificationsSlice.actions;

export default notificationsSlice.reducer;
